import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { NavLink } from "react-router-dom";
import * as StyledTypography from "@/styles/Styled.Typography";
import { fluid } from "@/styles/utils/fluid";

export const NavLayoutWrapper = styled.div`
  max-width: 1728px;
  min-width: 360px;
  position: relative;
  margin: ${({ theme }) => `
    0
    ${fluid({
      minSize: theme.spacing.vertical.xs,
      maxSize: theme.spacing.vertical.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })}
  `};
`;

export const LargeNavContainer = styled.div`
  width: 100%;
  z-index: 99;
  display: none;
  visibility: hidden;
  padding: 0;
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    display: block;
    visibility: visible;
    position: sticky;
    top: 0;
    background-color: ${({ theme }) => theme.colors.white};
    padding: ${({ theme }) => `
      0
      ${fluid({
        minSize: theme.spacing.vertical.lg,
        maxSize: theme.spacing.vertical.xl,
        minVW: theme.breakpointValues.lg,
        maxVW: theme.breakpointValues.xl,
        baseFontMin: theme.baseFont.lg,
        baseFontMax: theme.baseFont.xl,
      })}
    `};
  }
`;

export const NavStyle = styled(StyledTypography.TypoMenu1)`
  max-width: 1728px;
  min-width: 360px;
  width: 100%;
  height: 58px;
  position: relative;
  z-index: 100;
  background-color: ${({ theme }) => theme.colors.white};
  margin: 0;
  padding: 0;
  border-top: 4px solid ${({ theme }) => theme.colors.onyx};
  /* Custom Dashed Line */
  &::before {
    content: "";
    position: absolute;
    top: 4px;
    left: 0;
    width: 100%;
    height: 1px;
    background-image: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.onyx} 50%,
      rgba(255, 255, 255, 0) 0%
    );
    background-position: bottom;
    background-size: 8px 1px;
    background-repeat: repeat-x;
  }
  ul {
    list-style-type: none;
    margin: 0;
    padding: 8px 0;
    width: 100%;
  }
`;

export const LargeNav = styled(NavStyle)`
  display: none;
  visibility: hidden;
  position: relative;
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    display: flex;
    visibility: visible;
  }
  ul {
    /* lg to xl */
    @media ${({ theme }) => theme.breakpoints.xl} {
      display: flex;
      visibility: visible;
      flex-flow: row nowrap;
      align-items: flex-start;
      justify-content: center;
      gap: ${({ theme }) => theme.fontScale.majorSecond.menu}rem;
      border-bottom: 1px solid ${({ theme }) => theme.colors.onyx};
    }
  }
`;

export const MediumNavContainer = styled.div`
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 99;
  padding: ${({ theme }) => `
    0
    ${fluid({
      minSize: theme.spacing.vertical.xs,
      maxSize: theme.spacing.vertical.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })}
  `};
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    display: none;
    visibility: hidden;
  }
`;

export const MediumNav = styled(NavStyle)`
  display: none;
  visibility: hidden;
  /* sm to md */
  @media ${({ theme }) => theme.breakpoints.md} {
    display: flex;
    visibility: visible;
  }
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg} {
    display: flex;
    visibility: visible;
  }
  ul {
    /* sm to md */
    @media ${({ theme }) => theme.breakpoints.md} {
      display: flex;
      visibility: visible;
      flex-flow: row nowrap;
      align-items: flex-start;
      border-bottom: 1px solid ${({ theme }) => theme.colors.onyx};
      li {
        flex: 1;
        display: flex;
        justify-content: center;
        &:first-child > :first-of-type {
          margin-right: auto;
        }
        &:last-child > :last-of-type {
          margin-left: auto;
        }
      }
    }
    /* md and up */
    @media ${({ theme }) => theme.breakpoints.lg} {
      display: flex;
      visibility: visible;
      flex-flow: row nowrap;
      align-items: flex-start;
      border-bottom: 1px solid ${({ theme }) => theme.colors.onyx};
      li {
        flex: 1;
        display: flex;
        justify-content: center;
        &:first-child > :first-of-type {
          margin-right: auto;
        }
        &:last-child > :last-of-type {
          margin-left: auto;
        }
      }
    }
  }
`;

export const SmallNav = styled(NavStyle)`
  display: none;
  visibility: hidden;
  /* sm and up */
  @media ${({ theme }) => theme.breakpoints.sm} {
    display: flex;
    visibility: visible;
  }
  ul {
    /* sm and up */
    @media ${({ theme }) => theme.breakpoints.sm} {
      display: flex;
      visibility: visible;
      flex-flow: row nowrap;
      align-items: flex-start;
      justify-content: space-between;
      border-bottom: 1px solid ${({ theme }) => theme.colors.onyx};
    }
  }
`;

export const XtraSmallNav = styled(NavStyle)`
  display: none;
  visibility: hidden;
  /* xs only */
  @media ${({ theme }) => theme.breakpoints.xs} {
    display: flex;
    visibility: visible;
  }
  ul {
    /* xs only */
    @media ${({ theme }) => theme.breakpoints.xs} {
      display: flex;
      visibility: visible;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid ${({ theme }) => theme.colors.onyx};
    }
  }
`;

export const NavLinks = styled(NavLink)`
  ${StyledTypography.TypoMenuStyles};
  text-decoration: none;
  line-height: 36px;
  vertical-align: middle;
  background-image: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.imperialRed},
    ${({ theme }) => theme.colors.imperialRed} 50%,
    ${({ theme }) => theme.colors.onyx} 50%
  );
  background-size: 200% 100%;
  background-position: -100%;
  display: flex;
  padding: 0;
  position: relative;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: all 0.3s ease-in-out;
  &::before {
    content: "";
    background: ${({ theme }) => theme.colors.imperialRed};
    display: block;
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 0;
    height: 8px;
    transition: all 0.3s ease-in-out;
  }
  &:hover {
    background-position: 0;
    &::before {
      width: 100%;
    }
  }
  &.active {
    font-family: ${({ theme }) => theme.fontFace.itc_avant_garde_gothicbold};
  }
`;

export const MenuToggle = styled(NavLinks)`
  font-family: ${({ theme }) => theme.fontFace.itc_avant_garde_gothicmedium};
  letter-spacing: ${({ theme }) => theme.letterSpacing.menu};
  text-transform: uppercase;
  border: 0;
  transition: all 0.3s ease-in-out;
`;

export const IconToggleButton = styled.button`
  width: 36px;
  height: 36px;
  position: relative;
  border: 0;
  margin: 0;
  padding: 6px 12px 6px 0;
  overflow: hidden;
  background: transparent;
  color: inherit;
  -webkit-font-smoothing: inherit;
  appearance: none;
`;

export const IconHamburgerWrapper = styled.div`
  overflow: hidden;
  position: absolute;
  width: 24px;
  height: 24px;
  padding: 3px 0;
  z-index: 1;
  top: 50%;
  transform: translateY(-50%);
`;

export const IconCrossWrapper = styled.div`
  overflow: hidden;
  position: absolute;
  width: 24px;
  height: 24px;
  padding: 3px 0;
  z-index: 2;
  top: 50%;
  transform: translateY(-50%);
`;

export const IconHamburger = styled.span`
  display: block;
  width: 24px;
  height: 2px;
  border-radius: 2px;
  margin-bottom: 6px;
  background-color: ${({ theme }) => theme.colors.onyx};
  transform-origin: left;
  //transform: translateX(-36px);
  &:nth-child(3) {
    width: 16px;
    margin: 0;
  }
`;

export const IconCross = styled.span`
  position: absolute;
  //top: 50%; /* Center vertically */
  //left: 50%; /* Center horizontally */
  /* top: calc(50% - 25px);
    left: calc(50% + 25px); */
  top: -24px;
  left: 24px;
  display: block;
  width: 36px;
  height: 2px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.colors.onyx};
  transform-origin: center center;
  transform: rotate(-45deg);
  //transform: translate(0, 0) rotate(-45deg); /* Adjust transform */
  &:nth-child(2) {
    //left: calc(50% - 25px);
    left: -24px;
    //transform: translate(0, 0) rotate(45deg); /* Adjust transform */
    transform: rotate(45deg);
  }
`;

export const MenuLayer = styled.div`
  display: ${({ isMenuOpen }) => (isMenuOpen ? "block" : "none")};
  width: auto;
  position: absolute;
  overflow: auto;
  z-index: 80;
  left: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.vertical.xs,
      maxSize: theme.spacing.vertical.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
  right: ${({ theme }) =>
    fluid({
      minSize: theme.spacing.vertical.xs,
      maxSize: theme.spacing.vertical.xl,
      minVW: theme.breakpointValues.xs,
      maxVW: theme.breakpointValues.xl,
      baseFontMin: theme.baseFont.xs,
      baseFontMax: theme.baseFont.xl,
    })};
`;

export const MenuReveal1 = styled.div`
  background-color: ${({ theme }) => theme.colors.imperialRed};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 0;
  z-index: 81;
`;

export const MenuReveal2 = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 0;
  z-index: 82;
`;

export const DropMenuLinks = styled.ul`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  flex-flow: column wrap;
  //justify-content: center;
  align-items: center;
  padding: 0;
  z-index: 83;
`;

export const DropMenuLinksList = styled(StyledTypography.TypoMenu2)`
  padding: 0.5rem 0;
  overflow: hidden;
  width: 50%;
  height: 58px;
  text-align: center;
  position: relative;
  margin: 0;
  a {
    ${StyledTypography.TypoSubtitleStyles};
    text-transform: capitalize;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
  /* sm and up */
  @media ${({ theme }) => theme.breakpoints.md} {
    &:last-child {
      display: none;
      visibility: hidden;
    }
  }
  /* sm to md */
  @media ${({ theme }) => theme.breakpoints.md} {
    &:last-child {
      display: none;
      visibility: hidden;
    }
  }
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg} {
    &:last-child {
      display: none;
      visibility: hidden;
    }
  }
`;

export const Logo = styled.svg`
  height: auto;
  margin-top: 4px;
  /* xs only */
  @media ${({ theme }) => theme.breakpoints.xs} {
    width: 30px;
  }
  /* sm and up */
  @media ${({ theme }) => theme.breakpoints.sm} {
    width: 30px;
  }
  /* sm to md */
  @media ${({ theme }) => theme.breakpoints.md} {
    width: 80px;
  }
  /* md and up */
  @media ${({ theme }) => theme.breakpoints.lg} {
    width: 80px;
  }
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    width: 200px;
  }
`;

export const LogoLink = styled(Link)``;

export const LargeNavLogoBar = styled.div`
  width: 100%;
  margin: 0;
  justify-content: center;
  align-items: center;
  flex-flow: column nowrap;
  padding: ${({ theme }) => theme.spacing.element.xl}rem 0 0;
  position: relative;
  z-index: 90;
  display: none;
  visibility: hidden;
  /* lg to xl */
  @media ${({ theme }) => theme.breakpoints.xl} {
    display: flex;
    visibility: visible;
  }
`;

export const LargeNavLogoBarSlogan = styled(StyledTypography.TypoOverline2)`
  color: ${({ theme }) => theme.colors.imperialRed};
  padding-top: calc(${({ theme }) => theme.spacing.element.xs}rem / 2);
  padding-bottom: calc(${({ theme }) => theme.spacing.element.xl}rem / 2);
`;

export const ArticleMenuList = styled(StyledTypography.TypoBody1)`
  cursor: pointer;
`;
